// @flow
import * as R from "ramda"
import { differenceInMilliseconds, subHours, isAfter } from "date-fns"

import { get } from "common/utils/get"
import set from "common/utils/set"
import type { Airline } from "../Airline"
import type { Flight, DepartureArrival } from "../Flight/Flight"
import type { Part } from "../Part/Part"
import * as FlightUtils from "../Flight/utils"
import * as TransferUtils from "../Transfer/utils"
import defaultTrip from "./Trip"
import type { Trip, Stop } from "./Trip"
import type { PART_TYPE } from "../../../booking/scenes/ReservationForm/consts/reservationConsts"

// $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
export const createTrip = R.merge(defaultTrip)

export const getId = (trip: Trip): string =>
  trip.flights.reduce((res, flight) => res.concat([flight.id]), []).join("|")

// There should NEVER be trip with more flights, if the only flight is hidden
export const isHidden: Trip => boolean = R.compose(
  R.all(get("isHidden")),
  get("flights"),
)

export const getStopsCount = (trip: Trip): number => trip.flights.length - 1

export const isLayoverBetweenFlights = (part: Flight, trip: Trip): boolean => {
  const { parts } = trip
  const index = parts.indexOf(part)

  return part.type === "flight" && parts[index - 1].type === "flight"
}

export const getPrevStop = (part: Part, trip: Trip): Stop => {
  const { parts } = trip
  const index = parts.indexOf(part)
  const differentAirport =
    get([index - 1, "arrival", "where", "code"], parts) !==
    get(["departure", "where", "code"], part)

  const departureUtc = get(["departure", "when", "utc"], part)
  return index === 0
    ? {}
    : {
        name: get(["departure", "where", "name"], part),
        duration:
          departureUtc &&
          differenceInMilliseconds(departureUtc, get([index - 1, "arrival", "when", "utc"], parts)),
        bagsRecheckRequired: part.bagsRecheckRequired,
        hasGuarantee: part.guarantee,
        differentAirport: differentAirport
          ? {
              arrival: get([index - 1, "arrival", "where", "code"], parts),
              departure: get(["departure", "where", "code"], part),
            }
          : null,
        isLayoverBetweenFlights: isLayoverBetweenFlights(part, trip),
      }
}

export const getStops = (trip: Trip): Stop[] =>
  R.tail(trip.flights.map(flight => getPrevStop(flight, trip)))

export const getDeparture = (trip: Trip): DepartureArrival => {
  // $FlowIssue - we are sure we have at least 1 flight
  const firstFlight: Flight = R.head(trip.flights)
  return firstFlight.departure
}

export const getArrival = (trip: Trip): DepartureArrival => {
  // $FlowIssue - we are sure we have at least 1 flight
  const lastFlight: Flight = R.last(trip.flights)
  return lastFlight.arrival
}

export const getDuration = (trip: Trip) => {
  const arrival = getArrival(trip)
  const departure = getDeparture(trip)
  return arrival && departure && differenceInMilliseconds(arrival.when.utc, departure.when.utc)
}

export const getAllAirlines = (trip: Trip): { [string]: Airline } =>
  trip.flights.reduce((airlines, flight) => {
    const { code } = flight.airline
    const { type } = flight

    return set([code], set("type", type, flight.airline), airlines)
  }, {})

export const getFlightsCount: Trip => number = R.compose(
  R.length,
  get("flights"),
)

export const getTypes: Trip => PART_TYPE[] = R.compose(
  R.map(get("type")),
  get("flights"),
)

// Check whether departure is different day than arrival
export const isLocalOverNight = (trip: Trip): boolean => {
  const arrival = getArrival(trip)
  const departure = getDeparture(trip)

  return arrival && departure
    ? arrival.when.local.getMonth() !== departure.when.local.getMonth() ||
        arrival.when.local.getDate() !== departure.when.local.getDate()
    : false
}

// $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
export const isChangeAllowed = R.curry((hoursAllowedToChange: number, trip: Trip): boolean => {
  const departure = getDeparture(trip)
  const time = departure && departure.when.utc

  return time ? isAfter(subHours(time, hoursAllowedToChange), new Date()) : false
})

export const getPrioCabinBagsFlights: Trip => Flight[] = R.compose(
  R.filter(get("isCabinBagsWithPriorityBoarding")),
  get("flights"),
)

export const convertFromApi = ({ flights, parts }: any) => ({
  flights: flights.map(FlightUtils.convertFromApi),
  parts: parts.map(part =>
    part.type === "flight" ? FlightUtils.convertFromApi(part) : TransferUtils.convertFromApi(part),
  ),
})
